import { useState } from 'react';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle, Ruler, Palette, DollarSign, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CABINET_STYLES = [
  'Shaker',
  'Modern/Flat Panel',
  'Traditional/Raised Panel',
  'Transitional',
  'Farmhouse',
  'Not Sure - Need Guidance'
];

const BUDGET_RANGES = [
  'Under $5,000',
  '$5,000 - $10,000',
  '$10,000 - $20,000',
  '$20,000 - $30,000',
  '$30,000+',
  'Not Sure Yet'
];

export default function QuotePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    kitchen_dimensions: '',
    cabinet_style: '',
    budget_range: '',
    message: ''
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        data.append(key, formData[key]);
      });
      if (file) {
        data.append('file', file);
      }

      await axios.post(`${API}/quotes`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSubmitted(true);
      toast.success('Quote request submitted!', {
        description: "We'll contact you within 24 hours."
      });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Error submitting quote', {
        description: 'Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="quote-success">
          <div className="max-w-md text-center animate-scale-in">
            <CheckCircle size={64} className="mx-auto text-[#10B981] mb-6" />
            <h1 className="font-manrope text-3xl font-bold text-[#1A1A1A] mb-4">
              Quote Request Received!
            </h1>
            <p className="text-[#6B7280] mb-8">
              Thank you for your interest in Kitchen99. Our design team will review your request and contact you within 24 hours with a personalized quote.
            </p>
            <Button asChild>
              <a href="/shop">
                Browse Our Cabinets
                <ArrowRight className="ml-2" size={18} />
              </a>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="py-16 bg-[#F9F7F2]" data-testid="quote-hero">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl animate-fade-in-up">
            <span className="text-[#8B5E3C] text-sm font-medium uppercase tracking-wider">Free Design Service</span>
            <h1 className="font-manrope text-4xl sm:text-5xl font-bold text-[#1A1A1A] mt-4 mb-6">
              Get Your Free Kitchen Quote
            </h1>
            <p className="text-lg text-[#4B5563]">
              Fill out the form below and our design team will create a personalized quote for your kitchen renovation. No obligations, no pressure – just expert guidance.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16" data-testid="quote-form-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                {/* Contact Info */}
                <div>
                  <h2 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-4">
                    Contact Information
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Full Name *</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        data-testid="quote-name-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Email Address *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                        data-testid="quote-email-input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="form-label">Phone Number *</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(416) 555-0123"
                        required
                        data-testid="quote-phone-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Kitchen Details */}
                <div>
                  <h2 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-4">
                    Kitchen Details
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="form-label">Kitchen Dimensions *</label>
                      <Input
                        type="text"
                        name="kitchen_dimensions"
                        value={formData.kitchen_dimensions}
                        onChange={handleChange}
                        placeholder="e.g., 12ft x 10ft or approximate square footage"
                        required
                        data-testid="quote-dimensions-input"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Preferred Cabinet Style *</label>
                        <Select
                          value={formData.cabinet_style}
                          onValueChange={(value) => handleSelectChange('cabinet_style', value)}
                        >
                          <SelectTrigger data-testid="quote-style-select">
                            <SelectValue placeholder="Select a style" />
                          </SelectTrigger>
                          <SelectContent>
                            {CABINET_STYLES.map((style) => (
                              <SelectItem key={style} value={style}>{style}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="form-label">Budget Range *</label>
                        <Select
                          value={formData.budget_range}
                          onValueChange={(value) => handleSelectChange('budget_range', value)}
                        >
                          <SelectTrigger data-testid="quote-budget-select">
                            <SelectValue placeholder="Select budget" />
                          </SelectTrigger>
                          <SelectContent>
                            {BUDGET_RANGES.map((budget) => (
                              <SelectItem key={budget} value={budget}>{budget}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div>
                  <h2 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-4">
                    Additional Information
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="form-label">Tell us about your project</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Describe your dream kitchen, any specific requirements, timeline, etc."
                        rows={4}
                        data-testid="quote-message-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Upload Plans or Photos (Optional)</label>
                      <div className="border-2 border-dashed border-[#E5E7EB] rounded-md p-6 text-center hover:border-[#8B5E3C] transition-colors">
                        <input
                          type="file"
                          id="file-upload"
                          onChange={handleFileChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          data-testid="quote-file-input"
                        />
                        <label htmlFor="file-upload" className="cursor-pointer">
                          {file ? (
                            <div className="flex items-center justify-center gap-3 text-[#10B981]">
                              <FileText size={24} />
                              <span className="font-medium">{file.name}</span>
                            </div>
                          ) : (
                            <>
                              <Upload size={32} className="mx-auto text-[#6B7280] mb-2" />
                              <p className="text-[#6B7280]">
                                <span className="text-[#8B5E3C] font-medium">Click to upload</span> or drag and drop
                              </p>
                              <p className="text-sm text-[#9CA3AF] mt-1">
                                PDF, JPG, PNG (max 10MB)
                              </p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full sm:w-auto bg-[#1A1A1A] hover:bg-[#333333]"
                  disabled={loading}
                  data-testid="quote-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <>
                      Start Your Kitchen Design
                      <ArrowRight className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-[#F9F7F2] p-6">
                <h3 className="font-manrope text-lg font-semibold text-[#1A1A1A] mb-4">
                  What's Included
                </h3>
                <ul className="space-y-4">
                  <li className="flex gap-3">
                    <Ruler className="text-[#8B5E3C] flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-[#1A1A1A]">Kitchen Layout Review</p>
                      <p className="text-sm text-[#6B7280]">We'll analyze your space for optimal cabinet placement</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <Palette className="text-[#8B5E3C] flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-[#1A1A1A]">Style Recommendations</p>
                      <p className="text-sm text-[#6B7280]">Personalized cabinet suggestions based on your preferences</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <DollarSign className="text-[#8B5E3C] flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-[#1A1A1A]">Detailed Quote</p>
                      <p className="text-sm text-[#6B7280]">Itemized pricing with no hidden fees</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-[#1A1A1A] p-6 text-white">
                <h3 className="font-manrope text-lg font-semibold mb-2">
                  Need Immediate Help?
                </h3>
                <p className="text-gray-400 mb-4 text-sm">
                  Call us for instant consultation with our kitchen experts.
                </p>
                <a href="tel:+1-647-550-1313" className="text-[#8B5E3C] font-semibold text-lg">
                  +1-647-550-1313
                </a>
                <p className="text-gray-400 text-sm mt-1">
                  Mon-Fri: 9am-6pm EST
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
